import { noop } from 'lodash';
import type { FC, PropsWithChildren } from 'react';
import React from 'react';

import { ContainerWidthProvider } from '../../../../util/responsiveness';
import Flex from '../../../Flex';
import AppLayout from '../../AppLayout';
import AppLayoutRow from '../../AppLayoutRow';
import { useContentContainerContext } from '../../hooks';

import type { BackButtonProps } from './BackButton.types';
import HeaderRow from './HeaderRow';
import LoadableBackButtonRow from './LoadableBackButtonRow';
import MainAreaRow from './MainAreaRow';
import OuterLeftNav from './OuterLeftNav';
import SubHeaderRow from './SubHeaderRow';
import type { TemplateProps } from './Template.types';

const BackButtonRow: FC<PropsWithChildren<BackButtonProps>> = ({ ariaLabel, onClick, hasLeftNav }) => {
  const { shouldFillAvailableSpace } = useContentContainerContext();

  return !shouldFillAvailableSpace ? (
    <LoadableBackButtonRow hasLeftNav={hasLeftNav} ariaLabel={ariaLabel} onClick={onClick} />
  ) : null;
};

const Template: FC<PropsWithChildren<TemplateProps>> = ({
  header,
  headerDepth,
  subHeader,
  banner,
  hasHeroBackground,
  footer,
  openContentSidebarAriaLabel,
  closeContentSidebarAriaLabel,
  hasBackButton,
  backButtonAriaLabel = '',
  onBackButtonClick = noop,
  children,
  leftNav,
  outerLeftNav,
  backgroundColor,
  headerBackgroundColor,
  removeContentPadding = false,
  pinnedCollapsibleRightRail = false,
  scrollPaddingTop,
  scrollPaddingBottom,
}) => {
  const bannerRow = banner ? <AppLayoutRow>{banner}</AppLayoutRow> : null;

  const backButton = hasBackButton ? (
    <BackButtonRow hasLeftNav={!!leftNav} ariaLabel={backButtonAriaLabel} onClick={onBackButtonClick} />
  ) : null;

  return (
    <AppLayout
      disableNativePageScroll={true}
      closeContentSidebarAriaLabel={closeContentSidebarAriaLabel}
      openContentSidebarAriaLabel={openContentSidebarAriaLabel}
      hasBackButton={!!backButton}
      hasOuterLeftNav={!!outerLeftNav}
      height="100dvh"
      backgroundColor={backgroundColor}
      removeContentPadding={removeContentPadding}
      pinnedCollapsibleRightRail={pinnedCollapsibleRightRail}
    >
      <HeaderRow depth={headerDepth} backgroundColor={headerBackgroundColor}>
        {header}
      </HeaderRow>
      <SubHeaderRow>{subHeader}</SubHeaderRow>
      {backButton}
      {bannerRow}
      <AppLayoutRow grow={1}>
        <OuterLeftNav>{outerLeftNav}</OuterLeftNav>
        <Flex grow={1} direction="column" height="100%" maxHeight="100%">
          <ContainerWidthProvider>
            <MainAreaRow
              hasHeroBackground={hasHeroBackground}
              footer={footer}
              leftNav={leftNav}
              scrollPaddingTop={scrollPaddingTop}
              scrollPaddingBottom={scrollPaddingBottom}
            >
              {children}
            </MainAreaRow>
          </ContainerWidthProvider>
        </Flex>
      </AppLayoutRow>
    </AppLayout>
  );
};

export default Template;
